import React, { useMemo } from 'react';
import { TrafoStation, Abgangsleitung } from '../../types/network';
import { useStressTestStore } from '../../store/stressTestStore';
import { MeasureEditor } from './MeasureEditor';
import { findCablesConnectedToTrafo } from '../../utils/networkConnectionUtils';
import { detailStyles, headerColors } from '../../styles/detailStyles';
import { fmt, fmtOpt } from '../../utils/numberFormat';
import { ScenarioCouplingsSection } from './ScenarioCouplingsSection';
import { LOADING_WARNING_PCT } from '../../utils/loadingConstants';

interface ScenarioTrafoSidebarProps {
  elementId: string;
  onClose?: () => void;
}

/**
 * ScenarioTrafoSidebar - Simplified trafo view for stress test scenarios.
 * - Shows stress test results (power flow, loading)
 * - Shows connected cables (read-only)
 * - NO Lemonbeat measurements
 * - NO adding cables
 */
export const ScenarioTrafoSidebar: React.FC<ScenarioTrafoSidebarProps> = ({ elementId }) => {
  const stressElements = useStressTestStore(state => state.stressElements);
  const stressTestResults = useStressTestStore(state => state.stressTestResults);

  // Find the trafo element from stress test elements (isolated from main map zone)
  const element = useMemo(() => {
    return stressElements.find(e => e.id === elementId) as TrafoStation | undefined;
  }, [stressElements, elementId]);

  // Find connected cables
  const allCables = useMemo(() =>
    stressElements.filter((el): el is Abgangsleitung => el.type === 'abgangsleitung'),
    [stressElements]
  );

  const connectedLines = useMemo(() => {
    if (!element) return [];
    return findCablesConnectedToTrafo(element, allCables);
  }, [element, allCables]);

  if (!element) {
    return (
      <div className="p-4 text-center text-gray-500 dark:text-primary-400">
        Trafostation nicht gefunden
      </div>
    );
  }

  return (
    <div className={`flex flex-col ${detailStyles.layout.section}`}>
      {/* Header */}
      <div className={`${detailStyles.header.wrapper} ${headerColors.trafostation.bg}`}>
        <div className={detailStyles.header.content}>
          <div className="flex items-center space-x-3">
            <div className={detailStyles.header.iconBox}>
              <svg className={`${detailStyles.header.icon} ${headerColors.trafostation.icon}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <div>
              <h2 className={detailStyles.header.title}>Trafostation</h2>
              <p className={detailStyles.header.subtitle}>{element.name}</p>
            </div>
          </div>
          {/* Scenario mode indicator */}
          <div className="flex items-center gap-2">
            <span className="px-2 py-1 bg-orange-500/80 text-white text-xs font-medium rounded">
              Stresstest
            </span>
          </div>
        </div>
        <div className={`${detailStyles.header.id} ${headerColors.trafostation.id}`}>ID: {element.id}</div>
      </div>

      {/* Info Banner */}
      <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-md px-3 py-2">
        <p className="text-xs text-orange-800 dark:text-orange-200">
          Stresstest-Ansicht - Simulierte Werte
        </p>
      </div>

      {/* Trafo stress test results - basic values only */}
      {(() => {
        const trafoStress = stressTestResults?.results?.[elementId];
        if (!trafoStress) return null;
        const ratedKva = element.capacity;
        const actualKva = trafoStress.powerKva ?? 0;
        const loading = trafoStress.loadingPercent;
        const ratedCurrentA = ratedKva / (Math.sqrt(3) * 0.4);
        const lvCurrentA = ratedCurrentA * (loading / 100);
        return (
          <div className="rounded-lg p-3 border bg-gray-50 dark:bg-primary-700 border-gray-200 dark:border-primary-600">
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div className="text-gray-600 dark:text-primary-400">
                Wirkleistung: <span className="font-medium text-gray-900 dark:text-primary-100">{fmt((trafoStress.powerKw ?? 0), 1)} kW</span>
              </div>
              <div className="text-gray-600 dark:text-primary-400">
                Scheinleistung: <span className="font-medium text-gray-900 dark:text-primary-100">{fmt(actualKva, 1)} kVA</span>
              </div>
              <div className="text-gray-600 dark:text-primary-400">
                NS-Strom: <span className="font-medium text-gray-900 dark:text-primary-100">{fmt(lvCurrentA, 0)} A</span>
              </div>
              <div className="text-gray-600 dark:text-primary-400">
                Nennleistung: <span className="font-medium text-gray-900 dark:text-primary-100">{ratedKva} kVA</span>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Grid measure: transformer upgrade (scenario only) */}
      <MeasureEditor elementId={element.id} kind="trafo_upgrade" current={element.capacity ? Math.round(element.capacity / 1000) : undefined} />

      {/* Connected Cables Section (read-only) */}
      <div className="bg-white dark:bg-primary-800 rounded-lg shadow-sm overflow-hidden border border-gray-200 dark:border-primary-600">
        <div className="bg-blue-50 dark:bg-blue-900/20 px-4 py-2 border-b border-gray-200 dark:border-primary-600">
          <h3 className="text-sm font-medium text-blue-900 dark:text-blue-200">
            Angeschlossene Kabel ({connectedLines.length})
          </h3>
        </div>
        <div className="p-4">
          {connectedLines.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-primary-400 text-center py-2">
              Keine Kabel angeschlossen
            </p>
          ) : (
            <div className="space-y-2">
              {connectedLines.map((cable) => {
                // Stress test results are keyed by segment ID, not cable ID
                // Find the first segment with stress data
                const firstSegWithData = cable.segments?.find(seg => stressTestResults?.results?.[seg.id]);
                const segStressData = firstSegWithData ? stressTestResults?.results?.[firstSegWithData.id] : undefined;
                const isOverloaded = segStressData?.isOverloaded ?? false;
                const loadingPercent = segStressData?.loadingPercent ?? 0;

                return (
                  <div
                    key={cable.id}
                    className={`p-3 rounded-lg border ${
                      isOverloaded
                        ? 'bg-red-50 dark:bg-red-900/20 border-red-300 dark:border-red-700'
                        : 'bg-gray-50 dark:bg-primary-700 border-gray-200 dark:border-primary-600'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${
                          isOverloaded ? 'bg-red-500' : loadingPercent > LOADING_WARNING_PCT ? 'bg-yellow-500' : 'bg-green-500'
                        }`} />
                        <span className="text-sm font-medium text-gray-900 dark:text-primary-100">
                          {cable.name || cable.id}
                        </span>
                      </div>
                      <div className="text-right">
                        <span className={`text-sm font-semibold ${
                          isOverloaded
                            ? 'text-red-600 dark:text-red-400'
                            : loadingPercent > LOADING_WARNING_PCT
                              ? 'text-yellow-600 dark:text-yellow-400'
                              : 'text-green-600 dark:text-green-400'
                        }`}>
                          {fmt(loadingPercent, 1)}%
                        </span>
                      </div>
                    </div>
                    {segStressData && (
                      <div className="mt-2 space-y-2">
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          <div className="text-gray-600 dark:text-primary-400">
                            Strom: <span className="font-medium text-gray-900 dark:text-primary-100">
                              {fmtOpt(segStressData.currentA, 1) ?? '–'} A
                            </span>
                          </div>
                          <div className="text-gray-600 dark:text-primary-400">
                            Leistung: <span className="font-medium text-gray-900 dark:text-primary-100">
                              {segStressData.powerKva != null
                                ? `${fmt(Math.abs(segStressData.powerKva), 1)} kVA`
                                : `${fmt(Math.abs(segStressData.powerKw ?? 0), 1)} kW`}
                            </span>
                          </div>
                        </div>
                        {/* Per-phase current L1/L2/L3 */}
                        {(segStressData.currentL1A !== undefined || segStressData.currentL2A !== undefined || segStressData.currentL3A !== undefined) && (
                          <div className="grid grid-cols-3 gap-1 text-xs text-center">
                            <div className="bg-white/50 dark:bg-primary-600/50 rounded px-1 py-0.5">
                              <div className="text-[10px] text-gray-500 dark:text-gray-400">L1</div>
                              <div className="font-medium text-gray-900 dark:text-primary-100">
                                {fmtOpt(segStressData.currentL1A, 1) ?? '–'} A
                              </div>
                            </div>
                            <div className="bg-white/50 dark:bg-primary-600/50 rounded px-1 py-0.5">
                              <div className="text-[10px] text-gray-500 dark:text-gray-400">L2</div>
                              <div className="font-medium text-gray-900 dark:text-primary-100">
                                {fmtOpt(segStressData.currentL2A, 1) ?? '–'} A
                              </div>
                            </div>
                            <div className="bg-white/50 dark:bg-primary-600/50 rounded px-1 py-0.5">
                              <div className="text-[10px] text-gray-500 dark:text-gray-400">L3</div>
                              <div className="font-medium text-gray-900 dark:text-primary-100">
                                {fmtOpt(segStressData.currentL3A, 1) ?? '–'} A
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Trennstellen fused at this station (coupling cables of neighbour zones) */}
      <ScenarioCouplingsSection switchElementId={element.id} />
    </div>
  );
};
